import { useEffect, useState } from "react";
import api, { apiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { SEGMENT_STYLE, STATE_STYLE } from "../lib/states";
import LeadDrawer from "../components/LeadDrawer";
import { Search, Sparkles, AlertTriangle, Loader2 } from "lucide-react";

export default function Research() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [leads, setLeads] = useState([]);
  const [active, setActive] = useState(null);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [retrying, setRetrying] = useState(false);

  const load = () => api.get("/leads").then((r) => setLeads(r.data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const failedCount = leads.filter((l) => l.enrich_status === "failed").length;

  const retryFailed = async () => {
    setRetrying(true);
    try {
      const { data } = await api.post("/enrich/retry-failed");
      if (data.queued === 0) toast.info("Nothing to retry — all leads are already qualified.");
      else toast.success(`Re-running AI research for ${data.queued} lead(s)… this runs in the background.`);
      setTimeout(load, 3000);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setRetrying(false);
    }
  };

  let list = leads;
  if (filter === "failed") list = list.filter((l) => l.enrich_status === "failed");
  else if (filter === "unenriched") list = list.filter((l) => l.enrich_status !== "ok");
  else if (filter === "enriched") list = list.filter((l) => l.enrich_status === "ok");
  if (query) list = list.filter((l) => l.name?.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="flex flex-col h-full overflow-y-auto">
      <div className="h-16 shrink-0 px-6 flex items-center justify-between border-b border-stone-200">
        <div>
          <h1 className="font-heading text-xl tracking-tight text-stone-900">Research</h1>
          <p className="text-xs text-stone-500">Qualify leads by buyer type — privacy-first (name + city only)</p>
        </div>
        <div className="flex items-center gap-3">
          {isAdmin && failedCount > 0 && (
            <button onClick={retryFailed} disabled={retrying} data-testid="retry-failed-button"
              title="Re-run AI research for all failed + un-researched leads"
              className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white text-sm px-4 py-2 rounded-md transition-colors disabled:opacity-60">
              {retrying ? <Loader2 className="h-4 w-4 animate-spin" /> : <AlertTriangle className="h-4 w-4" />}
              Retry failed ({failedCount})
            </button>
          )}
          <div className="relative">
            <Search className="h-4 w-4 text-stone-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name…"
              data-testid="research-search"
              className="pl-9 pr-3 py-2 text-sm border border-stone-300 rounded-md bg-white w-56 focus:outline-none focus:ring-2 focus:ring-stone-900/60"
            />
          </div>
        </div>
      </div>

      <div className="px-6 pt-4 flex gap-2 flex-wrap">
        {[["all", "All"], ["unenriched", "Needs research"], ["enriched", "Qualified"], ["failed", `Failed${failedCount ? ` (${failedCount})` : ""}`]].map(([k, label]) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            data-testid={`research-filter-${k}`}
            className={`text-sm px-3 py-1.5 rounded-md border transition-colors ${
              filter === k ? "bg-stone-900 text-white border-stone-900" : "bg-white border-stone-300 text-stone-600 hover:border-stone-400"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <div className="p-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {list.map((l) => (
          <div
            key={l.id}
            onClick={() => setActive(l)}
            data-testid={`research-card-${l.id}`}
            className="bg-white border border-stone-200 rounded-xl p-4 hover:border-stone-400 hover:shadow-md transition-all cursor-pointer"
          >
            <div className="flex items-start justify-between">
              <p className="font-medium text-stone-900">{l.name}</p>
              <span className={`text-xs px-2 py-0.5 rounded-full border ${STATE_STYLE[l.state]?.badge}`}>{l.state}</span>
            </div>
            <span className={`inline-block mt-2 text-xs px-2 py-0.5 rounded-full border font-medium ${SEGMENT_STYLE[l.segment] || SEGMENT_STYLE.Unknown}`}>
              {l.segment || "Unknown"}
            </span>
            {l.profession ? (
              <p className="text-sm text-stone-600 mt-2">{l.profession} {l.employer ? `· ${l.employer}` : ""}</p>
            ) : l.enrich_status === "failed" ? (
              <p className="text-sm text-amber-700 mt-2 flex items-center gap-1.5" data-testid={`research-failed-${l.id}`}>
                <AlertTriangle className="h-3.5 w-3.5" /> AI research failed — retry pending
              </p>
            ) : l.enrich_status === "pending" ? (
              <p className="text-sm text-stone-900 mt-2 flex items-center gap-1.5">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Researching…
              </p>
            ) : (
              <p className="text-sm text-stone-400 mt-2 flex items-center gap-1.5"><Sparkles className="h-3.5 w-3.5" /> Not yet qualified</p>
            )}
          </div>
        ))}
        {list.length === 0 && <p className="text-sm text-stone-400 col-span-full text-center py-12">No leads to research.</p>}
      </div>

      {active && (
        <LeadDrawer
          leadId={active.id}
          isAdmin={isAdmin}
          onClose={() => setActive(null)}
          onSaved={load}
        />
      )}
    </div>
  );
}
